import { FormEvent, useCallback, useEffect, useReducer, useState } from "react";
import {
  IdTokenResult,
  ParsedToken,
  UserInfo,
  UserMetadata,
  User,
  AuthError,
  TotpSecret,
} from "firebase/auth";

import {
  AuthProviderProps,
  authReducer,
  Dispatch,
  AuthContext,
  SignupProps,
  AuthOptions,
  initialState,
} from "./auth-context";

import { GeolockDialog } from "~/components/geolock-dialog";
import { useIncidentBanner } from "~/components/IncidentBanner/useIncidentBanner";
import { Config } from "~/constants/configuration_provider";

export type DevUser = {
  name: string;
  email: string;
  token: string;
  provider: string;
  emailVerified: boolean;
};

// Default fallback user if server endpoint is not available
const defaultUser: DevUser = {
  name: "Jane Doe",
  email: "jane@mondoo.com",
  token: "jane-token",
  provider: "password",
  emailVerified: true,
};

// Users array - populated from server or uses default
export let users: DevUser[] = [defaultUser];

// Get the API endpoint from VITE_REGIONS config (format: "name,url|name,url")
const getApiEndpoint = (): string => {
  const regions = Config.VITE_REGIONS;
  if (!regions) return "";
  const firstRegion = regions.split("|")[0];
  const parts = firstRegion?.split(",");
  return parts?.[1] || "";
};

// Fetch users from server endpoint (available when demo mode is enabled)
export const fetchDevUsers = async (): Promise<DevUser[]> => {
  try {
    const apiEndpoint = getApiEndpoint();
    if (!apiEndpoint) return [defaultUser];

    const response = await fetch(`${apiEndpoint}/dev/users`);
    if (!response.ok) {
      return [defaultUser];
    }
    const fetchedUsers = await response.json();
    if (Array.isArray(fetchedUsers) && fetchedUsers.length > 0) {
      users = fetchedUsers;
      return fetchedUsers;
    }
  } catch (error) {
    console.error("Failed to fetch dev users:", error);
  }
  return [defaultUser];
};

// Selected user - set during provider initialization
export let selectedUser: DevUser = defaultUser;

// Method to store selected user in local storage and reload
export const selectUser = (usr: DevUser) => {
  console.log(`set user id: ${usr.token} name: ${usr.name}\n`);
  localStorage.setItem("user", JSON.stringify(usr));
  location.reload();
};

export class DevelopmentParsedToken implements ParsedToken {
  "exp"?: string;
  "sub"?: string;
  "auth_time"?: string;
  "iat"?: string;
  "firebase"?: {
    sign_in_provider?: string;
    sign_in_second_factor?: string;
  };
  [key: string]: string | object | undefined;
}

export class DevelopmentIdTokenResult implements IdTokenResult {
  authTime: string = "";
  expirationTime: string = "";
  issuedAtTime: string = "";
  signInProvider: string | null = "";
  signInSecondFactor: string | null = null;
  token: string = "";
  claims: ParsedToken = new DevelopmentParsedToken();
}

export class DevelopmentUser implements User {
  displayName: string | null = selectedUser.name;
  email: string | null = selectedUser.email;
  phoneNumber: string | null = null;
  photoURL: string | null = null;
  providerId: string = "github.com";
  uid: string = "";
  emailVerified: boolean = selectedUser.emailVerified;
  isAnonymous: boolean = false;
  metadata: UserMetadata = {};
  providerData: UserInfo[] = [
    {
      providerId: "LocalDev",
      uid: "LocalUID",
      displayName: selectedUser.name,
      email: selectedUser.email,
      phoneNumber: null,
      photoURL: null,
    },
  ];
  refreshToken: string = "";
  tenantId: string | null = "";

  delete(): Promise<void> {
    return Promise.resolve();
  }

  getIdToken(forceRefresh?: boolean): Promise<string> {
    return Promise.resolve(selectedUser.token);
  }

  getIdTokenResult(forceRefresh?: boolean): Promise<IdTokenResult> {
    return Promise.resolve(new DevelopmentIdTokenResult());
  }

  reload(): Promise<void> {
    return Promise.resolve();
  }

  toJSON(): object {
    return {};
  }
}

// Error codes can be found at
// https://firebase.google.com/docs/reference/js/v8/firebase.auth.Error#code
const handleErrorMessage = (errorCode: string) => {
  let customError = "Oops, something went wrong. Please give it another try.";
  switch (errorCode) {
    default:
      return customError;
  }
};

export function DevelopmentAuthProvider({ children }: AuthProviderProps) {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const [hasFetchedUser, setHasFetchedUser] = useState<boolean>(false);
  const { incident, showIncidentBanner, onCloseIncidentBanner } =
    useIncidentBanner();

  useEffect(() => {
    const initDevAuth = async () => {
      // 1. Fetch users from server (populates module-level `users` array)
      await fetchDevUsers();

      // 2. Check localStorage for previously selected user
      const storedUserJson = localStorage.getItem("user");
      if (storedUserJson) {
        try {
          selectedUser = JSON.parse(storedUserJson);
        } catch {
          selectedUser = users[0];
        }
      } else {
        selectedUser = users[0];
      }

      // 3. Create dev user instance with selected user data
      const devUser = new DevelopmentUser();

      // 4. Set auth state
      dispatch({
        type: "set user success",
        user: devUser,
        idTokenResult: null,
      });
      setHasFetchedUser(true);
    };

    initDevAuth();
  }, []);

  const logout = async () => {
    try {
      location.reload();
    } catch (error) {
      console.log("%c---- ERROR LOGGING OUT", "background: pink; color: black");
      const message = handleErrorMessage((error as AuthError).code);
      dispatch({ type: "set user failure", error: message });
    }
  };

  const loginWithProvider = async (
    event: FormEvent,
    dispatch: Dispatch,
    _providerOption: AuthOptions,
  ) => {
    event.preventDefault();
    dispatch({ type: "set user begin" });
  };

  const loginWithSSO = async (
    event: FormEvent,
    dispatch: Dispatch,
    _orgId: string,
  ) => {
    event.preventDefault();
    dispatch({ type: "set user begin" });
  };

  const signupWithEmailAndPassword = async (
    event: FormEvent,
    dispatch: Dispatch,
    _props: SignupProps,
  ) => {
    event.preventDefault();
    dispatch({ type: "set user begin" });
  };

  const loginWithEmailAndPassword = async (
    event: FormEvent,
    dispatch: Dispatch,
    _props: { email: string; password: string },
  ) => {
    event.preventDefault();
    dispatch({ type: "set user begin" });
  };

  const sendResetPasswordEmail = async (
    dispatch: Dispatch,
    _email: string,
    event?: FormEvent,
  ) => {
    event?.preventDefault();
    dispatch({ type: "reset password begin" });
  };

  const sendUserEmailVerification = async () => {};

  const changePassword = async (
    _oldPassword: string,
    _newPassword: string,
  ): Promise<void> => {
    const { user } = state;
    if (!user?.email) {
      return Promise.reject("Unable to update password");
    }

    return Promise.resolve();
  };

  const clearErrors = useCallback(() => {
    dispatch({ type: "clear errors" });
  }, []);

  const setGeolock = (isGeolocked: boolean) => {
    dispatch({ type: "set geolock", isGeolocked });
  };

  const reauthenticateForMfA = async (_params: { password?: string }) => {
    console.log("Reauthenticate for MFA called");
    return Promise.resolve();
  };

  const generateMfaSecrets = async () => {
    console.log("Generate MFA secrets called");
    return Promise.resolve({
      qrCodeUrl: "https://example.com/qrcode",
      secret: { secretKey: "mock-secret" } as TotpSecret,
    });
  };

  const enrollMfa = async (_params: {
    enrollmentSecret: TotpSecret;
    verificationCode: string;
  }) => {
    console.log("Enroll MFA called");
    return Promise.resolve();
  };

  const verifyMfaCodeForLogin = async (_params: {
    verificationCode: string;
  }) => {
    console.log("Verify MFA code for login called");
    return Promise.resolve();
  };

  const unenrollMfa = async (_params: { password?: string }) => {
    console.log("Unenroll MFA called");
    return Promise.resolve();
  };

  const verifyMfaCodeForUnenroll = async (_params: {
    verificationCode: string;
  }) => {
    console.log("Verify MFA code for unenroll called");
    return Promise.resolve();
  };

  const checkMFAStatus = (user: User | null) => {
    return false;
  };

  return (
    <AuthContext.Provider
      value={{
        state,
        dispatch,
        logout,
        loginWithProvider,
        signupWithEmailAndPassword,
        loginWithEmailAndPassword,
        loginWithSSO,
        sendResetPasswordEmail,
        sendUserEmailVerification,
        changePassword,
        clearErrors,
        setGeolock,
        handleErrorMessage,
        //mock mfa functions
        reauthenticateForMfA,
        generateMfaSecrets,
        enrollMfa,
        verifyMfaCodeForLogin,
        unenrollMfa,
        verifyMfaCodeForUnenroll,
        checkMFAStatus,
        incident: {
          data: incident,
          showIncidentBanner,
          onCloseIncidentBanner,
        },
      }}
    >
      {hasFetchedUser && children}
      {state.isGeolocked && <GeolockDialog />}
    </AuthContext.Provider>
  );
}
